import { useMemo, useState } from "react";
import { useData } from "../context/DataContext";
import {
  firmKpis, firmMonthly, revenueByStageMonthly, topProjects, months,
  backlogPipelineMonthly, backlogPipelineTotals, overAllocationAlert, invoicedVsForecastMonthly,
  grossNetSubMonthly,
} from "../lib/calc";
import { leaveByPersonMonth } from "../lib/leave";
import { fmtMoney, fmtPct, fmtMonthShort, contractLabel } from "../lib/format";
import { addMonths, currentMonthKey } from "../lib/dateWindow";
import { downloadCsv } from "../lib/csv";
import { downloadFirmSummaryHtml } from "../lib/essExport";
import InfoTip from "../components/InfoTip";
import ReportNotes from "../components/ReportNotes";
import SubmissionsWidget from "../components/SubmissionsWidget";
import { AlertTriangle, Download, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, RotateCcw } from "lucide-react";
import PageHeader from "../components/PageHeader";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend, ComposedChart, Line, Area,
} from "recharts";

const INK = "#0A0A0A";
const SOFT = "#A3A3A3";
const BACKLOG_FILL = "#0A0A0A";
const PIPELINE_FILL = "#C4B58A";
const INVOICED_FILL = "#5B7A66";
// Monthly chart (firm model): two lines — gross Total and Net — with the
// sub-consultant deduction read as the shaded gap between them.
const TOTAL_LINE = "#2F5AA8";   // blue  · gross fee to the client
const NET_LINE = "#A8342B";     // red   · what the firm keeps
const SUBS_BAND = "#2C6E49";    // green · sub-consultant deduction (band fill)
// Chart-window travel limits: two years back (firm reviews past performance
// month by month) and two years forward.
const SHIFT_MIN = -24;
const SHIFT_MAX = 24;
const SERIES_LABELS = {  total: "Total (gross forecast)",
  net: "Net (less sub-consultants)",
  band: "Shaded band = sub-consultant deduction",
};

// Shared chart geometry — identical plot margins and Y-axis gutter on every
// chart so the same month sits at the same x-position across all of them.
const CHART_MARGIN = { top: 8, right: 8, left: 0, bottom: 0 };
const Y_AXIS_WIDTH = 72;

// Stage palette — canonical stages get fixed tones; anything else falls back
// to the "Other" neutral.
const STAGE_COLORS = {
  "Feasibility": "#C4B58A",
  "Concept": "#8A9BB0",
  "Preliminary Design": "#5B7A66",
  "Developed Design": "#A8766B",
  "Detailed Design": "#0A0A0A",
  "Procurement": "#7A6A8A",
  "Site Observation": "#6E6E64",
  "Other": "#D9D5CC",
};
const stageColor = (s) => STAGE_COLORS[s] || STAGE_COLORS.Other;
const CONTRACT_COLORS = { Yes: "#A8C0AE", Tentative: "#D9CDB0", No: "#C99B95" };

export default function Overview() {
  const { rawData, data, monthsRange, scenario } = useData();
  const m = months(data);
  const activeMonths = useMemo(
    () => (monthsRange?.length ? monthsRange : (rawData?.meta?.months ?? [])),
    [monthsRange, rawData]
  );

  // Chart-window shift — LOCAL to the two Overview charts. KPI tiles above
  // stay pinned to the period selector's window (the firm's live position);
  // only the charts travel through past/future history so principals can
  // review any month without leaving the page. 0 = current window.
  const [chartShift, setChartShift] = useState(0);
  const chartMonths = useMemo(
    () => activeMonths.map((mo) => addMonths(mo, chartShift)),
    [activeMonths, chartShift],
  );
  const chartShifted = chartShift !== 0;
  const chartRangeLabel = chartMonths.length
    ? `${fmtMonthShort(chartMonths[0])} – ${fmtMonthShort(chartMonths[chartMonths.length - 1])}`
    : "";
  const stepShift = (delta) =>
    setChartShift((s) => Math.max(SHIFT_MIN, Math.min(SHIFT_MAX, s + delta)));

  // Money figures follow the scenario (`data`); the Live/Completed project
  // counts are taken from the raw dataset so they never move with the toggle.
  // Utilisation counts leave additively, matching the capacity matrix.
  const leaveFrac = useMemo(() => leaveByPersonMonth(rawData), [rawData]);
  const kpis = useMemo(() => firmKpis(data, rawData, leaveFrac), [data, rawData, leaveFrac]);
  const monthly = useMemo(() => firmMonthly(data, false), [data]);
  const byStage = useMemo(() => revenueByStageMonthly(data, activeMonths), [data, activeMonths]);
  const byStageChart = byStage.rows.map((r) => ({ ...r, month: fmtMonthShort(r.month) }));
  const top = useMemo(() => topProjects(data, 5), [data]);

  // Backlog vs Pipeline — independent of the scenario, but respects both the
  // period selector (activeMonths) AND the local chart shift for the chart
  // itself. The KPI tiles keep the un-shifted totals.
  const bp = useMemo(() => backlogPipelineMonthly(rawData, activeMonths), [rawData, activeMonths]);
  const bpTotals = useMemo(() => backlogPipelineTotals(rawData, activeMonths), [rawData, activeMonths]);
  const bpForChart = useMemo(() => backlogPipelineMonthly(rawData, chartMonths), [rawData, chartMonths]);
  const bpChart = bpForChart.map((r) => ({ month: fmtMonthShort(r.month), backlog: r.backlog, pipeline: r.pipeline }));

  // Contract status donut — built from the SAME projection as the Backlog and
  // Pipeline tiles (backlogPipelineTotals on the period window), so
  //     Confirmed slice = Total backlog   ·   Tentative slice = Pipeline value
  // hold to the cent for whatever period is selected. It therefore does NOT
  // follow the Confirmed/Forecast toggle, exactly like those two tiles.
  // Previously it summed every STORED month and skipped sub-consultant rows,
  // so it sat frozen at the all-time split and reconciled with nothing on the
  // page. The legacy "No" bucket is gone: scenario filtering strips those rows
  // upstream, so it could only ever read $0.
  const byContract = useMemo(() => ([
    { name: "Yes", value: bpTotals.backlog },
    { name: "Tentative", value: bpTotals.pipeline },
  ]), [bpTotals]);
  // Negative buckets (deduction rows tagged to a bucket) can't be drawn as an
  // arc — clamp for the geometry only; the list still prints the true figure.
  const contractPie = byContract.map((c) => ({ ...c, value: Math.max(0, c.value) }));
  const contractPieTotal = contractPie.reduce((s, x) => s + x.value, 0) || 1;

  const overAllocated = useMemo(() => overAllocationAlert(data, 3, leaveFrac), [data, leaveFrac]);

  // Invoiced vs forecast — raw actuals against the raw forecast, same graph.
  const ivf = useMemo(() => invoicedVsForecastMonthly(rawData, activeMonths), [rawData, activeMonths]);
  const ivfChart = ivf.map((r) => ({
    month: fmtMonthShort(r.month),
    forecast: r.forecast,
    invoiced: r.invoiced,
    variance: r.invoiced - r.forecast,
  }));
  const ivfTotals = ivf.reduce(
    (acc, r) => ({ forecast: acc.forecast + r.forecast, invoiced: acc.invoiced + r.invoiced }),
    { forecast: 0, invoiced: 0 }
  );

  // Shifted revenue series for the Monthly revenue chart. Uses the same
  // scenario-applied data but with the chartMonths window pushed through
  // firmMonthly by way of an override on data.meta.months.
  const monthlyShifted = useMemo(
    () => firmMonthly({ ...data, meta: { ...(data?.meta || {}), months: chartMonths } }, false),
    [data, chartMonths],
  );
  // Monthly data: gross Total and Net as lines, with the sub-consultant
  // deduction drawn as a shaded band filling the gap between them (`band` is a
  // [low, high] range pair — recharts renders an Area from a tuple dataKey).
  // `subs` stays on the row for the tooltip. Series follow the scenario toggle
  // (they read the scenario-applied `data`) and the chart-window controls.
  // Invoiced actuals are deliberately NOT plotted here (firm decision, Aug
  // 2026) — they live on the Invoiced vs Forecast chart below and in each
  // project form.
  //   subs = Σ|sub-consultant revenue| ≥ 0 and net = total − subs, so net ≤
  //   total always: the band can never invert, and a month with no subs gives
  //   net === total, i.e. a zero-height band that renders nothing.
  const gnsShifted = useMemo(() => grossNetSubMonthly(data, chartMonths), [data, chartMonths]);
  const monthlyChartData = gnsShifted.map((r) => ({
    month: fmtMonthShort(r.month),
    total: r.total,
    net: r.net,
    subs: r.subs,
    band: [r.net, r.total],
  }));
  // Monthly-revenue "over N months" total must equal what the chart plots —
  // the sum of the visible window under the active scenario. When the chart
  // is shifted, the total tracks the shifted window too.
  const total = chartMonths.reduce((s, mo) => s + (monthlyShifted[mo] || 0), 0);

  // Standalone HTML firm summary for Employee Share Scheme circulation.
  // Firm-wide figures and charts only — see lib/essExport.js. Every series is
  // computed on the PERIOD-SELECTOR window (activeMonths), not the local
  // chart-shift window, so every figure and chart agrees with the period the
  // document states and backlog + pipeline = forecast total holds. Built
  // client-side from the same memoised values the page renders, so it cannot
  // drift from the screen. Each field is mapped explicitly — no row object is
  // spread — so nothing beyond these numbers can reach the file. Nothing is
  // sent anywhere and nothing is stored.
  const gnsPeriod = useMemo(() => grossNetSubMonthly(data, activeMonths), [data, activeMonths]);

  const exportFirmSummary = () => {
    const scenarioTotal = activeMonths.reduce((s, mo) => s + (monthly[mo] || 0), 0);
    downloadFirmSummaryHtml({
      scenarioLabel: scenario === "confirmed" ? "Confirmed" : "Forecast",
      periodLabel: activeMonths.length
        ? `${fmtMonthShort(activeMonths[0])} – ${fmtMonthShort(activeMonths[activeMonths.length - 1])}`
        : "—",
      monthCount: activeMonths.length,
      generatedAt: new Date(),
      kpis: {
        backlog: bpTotals.backlog,
        pipeline: bpTotals.pipeline,
        scenarioTotal,
        liveProjects: kpis.activeProjects,
        avgUtilisation: kpis.avgUtilisation,
        overPersonMonths: kpis.overPersonMonths,
      },
      revenue: {
        total: scenarioTotal,
        rows: gnsPeriod.map((r) => ({
          label: fmtMonthShort(r.month), total: r.total, net: r.net, subs: r.subs,
        })),
      },
      backlogPipeline: {
        backlogTotal: bpTotals.backlog,
        pipelineTotal: bpTotals.pipeline,
        rows: bp.map((r) => ({ backlog: r.backlog, pipeline: r.pipeline })),
      },
      invoiced: {
        forecastTotal: ivfTotals.forecast,
        invoicedTotal: ivfTotals.invoiced,
        rows: ivf.map((r) => ({ forecast: r.forecast, invoiced: r.invoiced })),
      },
      stage: {
        stages: byStage.stages,
        rows: byStage.rows.map((r) =>
          Object.fromEntries(byStage.stages.map((s) => [s, r[s] || 0]))),
        colors: Object.fromEntries(byStage.stages.map((s) => [s, stageColor(s)])),
      },
      contract: byContract.map((c) => ({ name: c.name, value: c.value })),
    });
  };

  const exportTop5 = () => {
    const rows = [
      ["#", "Project", "ID", "Principal", "Contract", "Value"],
      ...top.map((p, i) => [
        i + 1, p.name, p.id,
        rawData?.principals.find((x) => x.id === p.principalId)?.name || "",
        p.contract, p.raw,
      ]),
    ];
    downloadCsv("top-projects.csv", rows);
  };

  return (
    <div className="space-y-8" data-testid="overview-view">
      <PageHeader
        section="OVERVIEW"
        title="Firm-wide forecast"
        subtitle="A live read of the practice — every principal's portfolio rolled up, backlog vs pipeline, invoicing against forecast, capacity signals."
        right={
          <button
            onClick={exportFirmSummary}
            title="Standalone HTML summary — four firm-level figures, for circulation to all staff"
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--rule-strong)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]"
            data-testid="export-firm-summary-html"
          >
            <Download size={12} /> Firm summary
          </button>
        }
      />
      {/* Sustained over-allocation alert */}
      {overAllocated.length > 0 && (
        <div
          className="flex items-start gap-4 p-5 border border-[var(--rule-strong)] bg-[#FBF3F2]"
          data-testid="over-allocation-alert-banner"
        >
          <AlertTriangle size={18} className="text-[#6B2F2A] mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="eyebrow text-[#6B2F2A] inline-flex items-center">
              Sustained over-allocation
              <InfoTip side="bottom" label="What 'Sustained over-allocation' means">
                Auto-flagged when a person is forecast over 100% capacity for <strong>3 or more consecutive months</strong>. <strong>Annual leave counts</strong>, added on top of booked load — so a stretch can tip over the line through absence as well as work. A persistent run is worth acting on; a single peak month usually is not.
              </InfoTip>
            </div>
            <div className="font-display text-sm mt-1 text-[var(--ink)]">
              <span className="font-mono-num">{overAllocated.length}</span> {overAllocated.length === 1 ? "person is" : "people are"} over 100% for 3+ consecutive months — check cover or re-staff the work.
            </div>
            <ul className="mt-2 text-xs font-mono-num text-[var(--muted-fg)]">
              {overAllocated.map((p) => (
                <li key={p.id} data-testid={`over-allocated-${p.id}`}>
                  · {p.name} · {p.streak} months ({fmtMonthShort(p.fromMonth)} → {fmtMonthShort(p.toMonth)})
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* KPI strip */}
      <section className="border border-[var(--rule)]">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-y md:divide-y-0 md:divide-x divide-[var(--rule)]">
          <Kpi label="Total backlog" value={fmtMoney(bpTotals.backlog, { compact: true })} testid="kpi-backlog" sub="contract: yes · both scenarios"
               warnNote={bpTotals.backlog < 0 ? "Negative total — contains deduction rows tagged Confirmed; check classifications." : undefined}
               info={<><strong>Does not follow the Scenario toggle</strong> — Backlog and Pipeline are the firm's standing position, so they read the same under Confirmed and Forecast. Only the third tile follows the toggle.<br/><br/>Signed work across the visible window ({m.length} months) — every scope row where <strong>contract = Yes</strong>, sub-consultant deductions included. Classification is per scope, so a mixed project contributes its Confirmed rows here and its Tentative rows to Pipeline. Identical to the Confirmed total by construction.</>} />
          <Kpi label="Pipeline value" value={fmtMoney(bpTotals.pipeline, { compact: true })} testid="kpi-pipeline" sub="contract: tentative · both scenarios"
               warnNote={bpTotals.pipeline < 0 ? "Negative total — contains deduction rows tagged Tentative; check classifications." : undefined}
               info={<><strong>Does not follow the Scenario toggle</strong> — Backlog and Pipeline are the firm's standing position, so they read the same under Confirmed and Forecast. Only the third tile follows the toggle.<br/><br/>What the <strong>Tentative</strong> rows add on top of Backlog, across the same window — i.e. Forecast total − Backlog. Classification is per scope.</>} />
          <Kpi label={scenario === "confirmed" ? "Confirmed total" : "Forecast total"} value={fmtMoney(total, { compact: true })} testid="kpi-scenario-total" sub={scenario === "confirmed" ? "= backlog · follows scenario" : "= backlog + pipeline · follows scenario"}
               info={<><strong>Follows the Scenario toggle</strong> — this is the only money tile that does. Total fees under the selected Scenario, summed across the visible window. With <strong>Confirmed</strong> this equals Backlog; with <strong>Forecast</strong> this equals Backlog + Pipeline. Both hold to the cent, per month — Backlog and Pipeline are derived from the same projection this tile uses.</>} />
          <Kpi label="Live projects" value={kpis.activeProjects} testid="kpi-projects"
               sub={kpis.completedProjects ? `+ ${kpis.completedProjects} completed` : "all live work"}
               info={<>Projects whose status is not <strong>Completed</strong>, counted across the whole portfolio regardless of the Confirmed/Forecast toggle — only money figures follow the scenario. Completed work is excluded from this count only; it still counts in every money figure, chart and history above.</>} />
          <Kpi label="Avg utilisation" value={fmtPct(kpis.avgUtilisation)} testid="kpi-avg-util" sub="incl. leave · all live work"
               info={<>Average team capacity used across every person × every month in view. 100% = the team is fully booked on average. Counts resourcing on ALL live projects regardless of the Confirmed/Forecast toggle — only money figures follow the scenario.<br/><br/><strong>Annual leave counts towards utilisation</strong>, added on top of booked load exactly as the capacity matrix shows it — a month booked to 80% with a full week away counts as 105%.</>} />
          <Kpi label="Over-allocated p-months" value={kpis.overPersonMonths} testid="kpi-over" warn={kpis.overPersonMonths > 0} sub="incl. leave · all live work"
               info={<>Count of person-months where someone is over 100%. Each instance is one person, one month — so '20' could be 5 people overloaded for 4 months each, or 2 people for 10 months. Counts ALL live projects regardless of the scenario toggle.<br/><br/><strong>Annual leave counts</strong>, added on top of booked load — so a month can tip over 100% on leave alone, matching the red cells on the capacity matrix.</>} />
        </div>
      </section>

      {/* Free-form notes for this forecast period */}
      <ReportNotes />

      {/* Submissions status — who has updated their projects this month */}
      <SubmissionsWidget />

      {/* Monthly revenue (scenario) */}
      <section>
        <div className="flex justify-between items-end mb-5">
          <div>
            <div className="eyebrow flex items-center gap-2">By month · scenario{chartShifted && (
              <span className="pill pill-info font-mono-num text-[10px] normal-case tracking-[0.06em]" data-testid="chart-window-indicator">
                Viewing: {chartRangeLabel}
              </span>
            )}</div>
            <h2 className="section-h mt-1 inline-flex items-center">
              Monthly revenue
              <InfoTip side="bottom" label="How this total is calculated">
                Sum of every scope's monthly revenue for the visible window under the active scenario ({scenario === "confirmed" ? "Confirmed = only contract Yes" : "Forecast = contract Yes + Tentative"}). Sub-consultants, time-charge and disbursements are included as scope stages. Invoiced actuals are NOT plotted here — see the Invoiced vs Forecast chart below.<br/><br/>
                Identity: with <strong>Confirmed</strong> scenario this must equal <strong>Backlog</strong> for the same window; with <strong>Forecast</strong> it must equal <strong>Backlog + Pipeline</strong>.<br/><br/>
                <strong>Scroll history</strong>: the arrows below shift only these two charts by one or three months at a time — the KPI tiles above stay pinned to the firm's live position so you can compare a past window against today's numbers.
              </InfoTip>
            </h2>
          </div>
          <ChartWindowShift shift={chartShift} onStep={stepShift} onReset={() => setChartShift(0)} />
        </div>
        <div className="border border-[var(--rule)] p-4">
          <div className="font-mono-num text-3xl mb-1" data-testid="overview-period-total">
            {fmtMoney(total)}
            <span className="text-[var(--soft-fg)] text-xs ml-3">over {chartMonths.length} months · {scenario === "confirmed" ? "Confirmed only" : "Confirmed + Tentative"}</span>
          </div>
          <div className="t-caption mb-4" data-testid="overview-period-sublabel">
            Two lines: <span className="text-[#2F5AA8]">Total</span> is the gross fee to clients and
            <span className="text-[#A8342B]"> Net</span> is what the firm keeps. The
            <span className="text-[#2C6E49]"> shaded green band</span> between them is the
            sub-consultant deduction — where the lines meet, there are no sub-consultant costs that
            month. Hover any month for the figure. The headline figure above is the net position;
            invoiced actuals are shown on the Invoiced vs Forecast chart below.
          </div>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <ComposedChart data={monthlyChartData} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="2 4" stroke="#EEE" vertical={false} />
                {/* Bar-driven band scale (invisible zero-width bar) so month
                    ticks centre exactly as they do on the bar charts below —
                    same month, same x-position on every Overview chart. */}
                <XAxis dataKey="month" stroke={SOFT} fontSize={11} tickLine={false} axisLine={{ stroke: "#E5E5E5" }} />
                <YAxis width={Y_AXIS_WIDTH} stroke={SOFT} fontSize={11} tickLine={false} axisLine={false}
                  tickFormatter={(v) => fmtMoney(v, { compact: true })} />
                <Tooltip content={<MonthlyRevenueTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => SERIES_LABELS[v] || v} />
                <Bar dataKey="__axisAlign" barSize={0} legendType="none" isAnimationActive={false} tooltipType="none" />
                {/* Band first so both lines draw over it. Semi-transparent so
                    the gridlines stay legible through the fill. legendType
                    "rect" gives it a filled swatch, reading as a band rather
                    than a series. */}
                <Area type="monotone" dataKey="band" stroke="none" fill={SUBS_BAND}
                      fillOpacity={0.16} legendType="rect" isAnimationActive={false}
                      activeDot={false} />
                <Line type="monotone" dataKey="total" stroke={TOTAL_LINE} strokeWidth={1.8} dot={{ r: 2, fill: TOTAL_LINE }} activeDot={{ r: 4 }} />
                <Line type="monotone" dataKey="net" stroke={NET_LINE} strokeWidth={1.8} dot={{ r: 2, fill: NET_LINE }} activeDot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Backlog vs Pipeline */}
      <section>
        <div className="flex justify-between items-end mb-5 flex-wrap gap-3">
          <div>
            <div className="eyebrow flex items-center gap-2">Certainty over time{chartShifted && (
              <span className="pill pill-info font-mono-num text-[10px] normal-case tracking-[0.06em]" data-testid="chart-window-indicator-bp">
                Viewing: {chartRangeLabel}
              </span>
            )}</div>
            <h2 className="section-h mt-1 inline-flex items-center">
              Backlog vs Pipeline
              <InfoTip side="bottom" label="Backlog vs Pipeline explained">
                <strong>Backlog</strong> is signed work (Contract = Confirmed). <strong>Pipeline</strong> is likely-but-unsigned work (Contract = Tentative). Watching them month-by-month shows how much of next year's revenue is locked in vs still in play.
              </InfoTip>
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-xs font-mono-num text-[var(--soft-fg)]">
              <LegendDot color={BACKLOG_FILL} label="Backlog · Yes" />
              <span className="mx-3">·</span>
              <LegendDot color={PIPELINE_FILL} label="Pipeline · Tentative" />
            </div>
            <ChartWindowShift shift={chartShift} onStep={stepShift} onReset={() => setChartShift(0)} />
          </div>
        </div>
        <div className="border border-[var(--rule)]">
          <div className="grid grid-cols-2 divide-x divide-[var(--rule)] border-b border-[var(--rule)]">
            <div className="px-4 py-3">
              <div className="eyebrow">Backlog total</div>
              <div className="font-mono-num text-2xl mt-1" data-testid="bp-backlog-total">{fmtMoney(bpTotals.backlog)}</div>
            </div>
            <div className="px-4 py-3">
              <div className="eyebrow text-[var(--soft-fg)]">Pipeline total</div>
              <div className="font-mono-num text-2xl mt-1 text-[var(--soft-fg)]" data-testid="bp-pipeline-total">{fmtMoney(bpTotals.pipeline)}</div>
            </div>
          </div>
          <div className="p-4" style={{ height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={bpChart} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="2 4" stroke="#EEE" vertical={false} />
                <XAxis dataKey="month" stroke={SOFT} fontSize={11} tickLine={false} axisLine={{ stroke: "#E5E5E5" }} />
                <YAxis width={Y_AXIS_WIDTH} stroke={SOFT} fontSize={11} tickLine={false} axisLine={false}
                  tickFormatter={(v) => fmtMoney(v, { compact: true })} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtMoney(v)} />
                <Bar dataKey="backlog" fill={BACKLOG_FILL} barSize={14} />
                <Bar dataKey="pipeline" fill={PIPELINE_FILL} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Invoiced vs Forecast — actuals against plan, same graph */}
      <section data-testid="invoiced-vs-forecast">
        <div className="flex justify-between items-end mb-5 flex-wrap gap-3">
          <div>
            <div className="eyebrow">Plan vs cash</div>
            <h2 className="section-h mt-1 inline-flex items-center">
              Invoiced vs Forecast
              <InfoTip side="bottom" label="Invoiced vs Forecast">
                <strong>Forecast</strong> is the raw monthly fee plan from every project's scope grid. <strong>Invoiced</strong> is what principals recorded as actually invoiced (project form → Invoicing · actuals vs forecast). Bars under the line = billing behind plan.
              </InfoTip>
            </h2>
          </div>
          <div className="text-xs font-mono-num text-[var(--soft-fg)]">
            <LegendDot color={INVOICED_FILL} label="Invoiced · actual" />
            <span className="mx-3">·</span>
            <LegendDot color={INK} label="Forecast · plan" />
          </div>
        </div>
        <div className="border border-[var(--rule)] p-4">
          <div className="flex items-end gap-10 flex-wrap">
            <div>
              <div className="eyebrow">Forecast total</div>
              <div className="font-mono-num text-3xl mt-1" data-testid="ivf-forecast-total">{fmtMoney(ivfTotals.forecast)}</div>
            </div>
            <div>
              <div className="eyebrow text-[var(--soft-fg)]">Invoiced to date</div>
              <div className="font-mono-num text-3xl mt-1 text-[var(--soft-fg)]" data-testid="ivf-invoiced-total">{fmtMoney(ivfTotals.invoiced)}</div>
            </div>
          </div>
          <div className="t-caption mt-2 mb-4" data-testid="ivf-sublabel">
            Forecast is the full {activeMonths.length}-month plan; invoicing catches up month by month,
            so the two totals are not meant to match. Hover any month for that month's forecast,
            invoiced and the difference between them.
          </div>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <ComposedChart data={ivfChart} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="2 4" stroke="#EEE" vertical={false} />
                <XAxis dataKey="month" stroke={SOFT} fontSize={11} tickLine={false} axisLine={{ stroke: "#E5E5E5" }} />
                <YAxis width={Y_AXIS_WIDTH} stroke={SOFT} fontSize={11} tickLine={false} axisLine={false}
                  tickFormatter={(v) => fmtMoney(v, { compact: true })} />
                <Tooltip content={<IvfTooltip />} />
                <Bar dataKey="invoiced" fill={INVOICED_FILL} barSize={16} />
                <Line type="monotone" dataKey="forecast" stroke={INK} strokeWidth={1.5}
                      dot={{ r: 2, fill: INK }} activeDot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* By stage + Contract certainty */}
      {/* Revenue by stage over time — stacked monthly production */}
      <section data-testid="stage-monthly-section">
        <div className="flex justify-between items-end mb-5 flex-wrap gap-3">
          <div>
            <div className="eyebrow">Split over time</div>
            <h2 className="section-h mt-1 inline-flex items-center">
              Revenue by stage · monthly
              <InfoTip side="bottom" label="Stage rollup">
                Each bar is a month's total production, split by design stage. Only stages principals actually submitted are shown — custom scope labels roll up into the canonical stages, and anything that doesn't match lands in <strong>Other</strong>.
              </InfoTip>
            </h2>
          </div>
          <div className="flex items-center gap-4 flex-wrap text-xs font-mono-num">
            {byStage.stages.map((s) => <LegendDot key={s} color={stageColor(s)} label={s} />)}
          </div>
        </div>
        <div className="border border-[var(--rule)] p-4" style={{ height: 320 }}>
          <ResponsiveContainer>
            <BarChart data={byStageChart} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="2 4" stroke="#EEE" vertical={false} />
              <XAxis dataKey="month" stroke={SOFT} fontSize={11} tickLine={false} axisLine={{ stroke: "#E5E5E5" }} />
              <YAxis width={Y_AXIS_WIDTH} stroke={SOFT} fontSize={11} tickLine={false} axisLine={false}
                tickFormatter={(v) => fmtMoney(v, { compact: true })} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [fmtMoney(v), name]} />
              {byStage.stages.map((s) => (
                <Bar key={s} dataKey={s} stackId="stage" fill={stageColor(s)} barSize={26} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Contract certainty — same window and same projection as the Backlog
          and Pipeline tiles, so the two slices reconcile with them exactly. */}
      <section data-testid="contract-status-section">
        <div>
          <div className="eyebrow mb-2">Certainty</div>
          <h2 className="section-h mb-4 inline-flex items-center">
            Contract status · period
            <InfoTip side="bottom" label="About Contract status">
              <strong>Does not follow the Scenario toggle</strong> — like the Backlog and Pipeline tiles, this is the firm's standing position, so it reads the same under Confirmed and Forecast.<br/><br/>
              Built from the same projection those tiles use, across the selected period ({activeMonths.length} {activeMonths.length === 1 ? "month" : "months"}): <strong>Confirmed</strong> equals <strong>Total backlog</strong> and <strong>Tentative</strong> equals <strong>Pipeline value</strong>, to the cent. Classification is per scope row, so a mixed project splits across both slices, and sub-consultant fees deduct inside whichever slice carries them.
            </InfoTip>
          </h2>
          <div className="border border-[var(--rule)] p-4 flex items-center gap-6" style={{ height: 280 }}>
            <div style={{ width: 200, height: 200 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={contractPie} dataKey="value" innerRadius={55} outerRadius={88} stroke="#FFF" strokeWidth={2}>
                    {contractPie.map((c) => <Cell key={c.name} fill={CONTRACT_COLORS[c.name]} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtMoney(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1">
              <ul className="space-y-3">
                {byContract.map((c) => (
                  <li key={c.name} className="flex items-center justify-between text-sm" data-testid={`contract-row-${c.name.toLowerCase()}`}>
                    <span className="flex items-center gap-3">
                      <span style={{ width:10, height:10, background: CONTRACT_COLORS[c.name] }} />
                      <span className="font-display tracked-tight uppercase text-xs">{contractLabel(c.name)}</span>
                    </span>
                    <span className="font-mono-num text-[var(--ink)]" data-testid={`contract-value-${c.name.toLowerCase()}`}>
                      {fmtMoney(c.value, { compact: true })}
                      <span className="text-[var(--soft-fg)] ml-2">{fmtPct(Math.max(0, c.value)/contractPieTotal)}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <div className="t-caption mt-4" data-testid="contract-status-caption">
                Same {activeMonths.length}-month window as the tiles above — Confirmed reads the
                Total backlog figure and Tentative the Pipeline value. Unchanged by the
                Confirmed/Forecast toggle.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Top 5 projects */}
      <section>
        <div className="flex justify-between items-end mb-4">
          <div>
            <div className="eyebrow">Top 5</div>
            <h2 className="section-h mt-1">Highest-value projects</h2>
          </div>
          <button
            onClick={exportTop5}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--rule-strong)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]"
            data-testid="export-top5-csv"
          >
            <Download size={12} /> CSV
          </button>
        </div>
        <div className="border border-[var(--rule)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="eyebrow text-left">
                <th className="px-4 py-3 w-8">#</th>
                <th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Contract</th>
                <th className="px-4 py-3 text-right">Value</th>
              </tr>
            </thead>
            <tbody>
              {top.map((p, i) => {
                const chip = p.contract === "Yes" ? "chip-yes" : p.contract === "Tentative" ? "chip-tentative" : "chip-no";
                return (
                  <tr key={p.id} className="border-t border-[var(--rule)]" data-testid={`top-project-${i}`}>
                    <td className="px-4 py-3 font-mono-num text-[var(--soft-fg)]">{String(i+1).padStart(2,"0")}</td>
                    <td className="px-4 py-3">{p.name}</td>
                    <td className="px-4 py-3"><span className={`chip ${chip}`}>{contractLabel(p.contract)}</span></td>
                    <td className="px-4 py-3 text-right font-mono-num">{fmtMoney(p.raw)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const tooltipStyle = {
  background: "#fff",
  border: "1px solid #E5E5E5",
  borderRadius: 0,
  fontFamily: "IBM Plex Mono",
  fontSize: 11,
};

// Monthly revenue tooltip. The sub-consultant figure no longer has a line of
// its own (it reads as the shaded band between Total and Net), so it is spelled
// out here rather than left to the default series-driven tooltip.
function MonthlyRevenueTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div style={tooltipStyle} className="px-3 py-2" data-testid="monthly-revenue-tooltip">
      <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--soft-fg)] mb-1">{label}</div>
      <div className="flex justify-between gap-8" style={{ color: TOTAL_LINE }}>
        <span>Total (gross)</span><span>{fmtMoney(row.total)}</span>
      </div>
      <div className="flex justify-between gap-8" style={{ color: NET_LINE }}>
        <span>Net</span><span>{fmtMoney(row.net)}</span>
      </div>
      <div className="flex justify-between gap-8 mt-1 pt-1 border-t border-[#E5E5E5]"
           style={{ color: SUBS_BAND }}>
        <span>Sub-consultants</span>
        <span data-testid="tooltip-subs-value">{row.subs ? `− ${fmtMoney(row.subs)}` : fmtMoney(0)}</span>
      </div>
    </div>
  );
}

// Invoiced vs Forecast tooltip — that month's forecast, invoiced and the
// difference. Replaces the standing Variance tile, which compared a full
// year's plan against invoicing-to-date and read alarmingly negative.
function IvfTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const diff = row.variance || 0;
  return (
    <div style={tooltipStyle} className="px-3 py-2" data-testid="ivf-tooltip">
      <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--soft-fg)] mb-1">{label}</div>
      <div className="flex justify-between gap-8"><span>Forecast</span><span>{fmtMoney(row.forecast)}</span></div>
      <div className="flex justify-between gap-8"><span>Invoiced</span><span>{fmtMoney(row.invoiced)}</span></div>
      <div className="flex justify-between gap-8 mt-1 pt-1 border-t border-[#E5E5E5]"
           style={{ color: diff < -0.5 ? "#6B2F2A" : diff > 0.5 ? "#2C4A38" : undefined }}>
        <span>Difference</span>
        <span>{diff > 0 ? "+" : diff < 0 ? "−" : ""}{fmtMoney(Math.abs(diff))}</span>
      </div>
    </div>
  );
}

// Every tile has identical geometry: label block reserves two lines (so a
// wrapping label can never push its number down), value sits on a shared
// baseline, and the sub-caption slot is always reserved. Left-aligned.
function Kpi({ label, value, testid, warn, sub, info, warnNote }) {
  return (
    <div className="kpi flex flex-col text-left" data-testid={testid}>
      <div className="kpi-label flex items-start gap-0 min-h-[2.5em] leading-[1.25]">
        <span>{label}</span>
        {info && <InfoTip side="bottom" label={`About ${label}`}>{info}</InfoTip>}
      </div>
      <div className={`kpi-value flex items-center gap-2 leading-none ${warn ? "text-[#6B2F2A]" : ""}`}>
        {value}
        {warnNote && (
          <span
            className="inline-flex items-center text-[#B45309]"
            title={warnNote}
            data-testid={`${testid}-warn`}
          >
            <AlertTriangle size={13} strokeWidth={2.2} />
          </span>
        )}
      </div>
      <div className="eyebrow mt-1.5 normal-case tracking-[0.08em] text-[var(--soft-fg)] min-h-[1.15em]">
        {sub || "\u00A0"}
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span style={{ width: 10, height: 10, background: color, display: "inline-block" }} />
      <span className="text-[var(--muted-fg)]">{label}</span>
    </span>
  );
}

// Chart-window shift control. Steps: back a year / quarter / month, Today
// (reset), forward a month / quarter / year. Travel is clamped to the past two
// years and two years forward. Only affects the charts it's placed on; KPI
// tiles stay pinned to the period selector's window.
function ChartWindowShift({ shift, onStep, onReset }) {
  const btn = "p-1.5 border border-[var(--rule)] hover:bg-[var(--paper-soft)] text-[var(--muted-fg)] disabled:opacity-40 disabled:cursor-default";
  const atBack = shift <= SHIFT_MIN;
  const atFwd = shift >= SHIFT_MAX;
  return (
    <div className="inline-flex items-center gap-1" data-testid="chart-window-shift">
      <button className={`${btn} font-display tracked-tight text-[10px] uppercase px-2`} onClick={() => onStep(-12)} disabled={atBack}
        title="Back a year" data-testid="chart-shift-year-back">−1Y</button>
      <button className={btn} onClick={() => onStep(-3)} disabled={atBack} title="Back a quarter" data-testid="chart-shift-quarter-back"><ChevronsLeft size={14} /></button>
      <button className={btn} onClick={() => onStep(-1)} disabled={atBack} title="Back a month" data-testid="chart-shift-back"><ChevronLeft size={14} /></button>
      <button
        className={`${btn} font-display tracked-tight text-[10px] uppercase px-2`}
        onClick={onReset}
        disabled={shift === 0}
        title="Snap to current rolling window"
        data-testid="chart-shift-today"
      >
        <RotateCcw size={11} className="inline-block mr-1" /> Today
      </button>
      <button className={btn} onClick={() => onStep(1)} disabled={atFwd} title="Forward a month" data-testid="chart-shift-forward"><ChevronRight size={14} /></button>
      <button className={btn} onClick={() => onStep(3)} disabled={atFwd} title="Forward a quarter" data-testid="chart-shift-quarter-forward"><ChevronsRight size={14} /></button>
      <button className={`${btn} font-display tracked-tight text-[10px] uppercase px-2`} onClick={() => onStep(12)} disabled={atFwd}
        title="Forward a year" data-testid="chart-shift-year-forward">+1Y</button>
    </div>
  );
}

